import { ReactNode, useEffect } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import {
  defaultLocale,
  fallbackLocale,
  supportLocales,
  translate,
} from 'infras/i18n';
import { imagePath } from 'presentations/assets';
import { webPath } from 'presentations/routes/path';
import { GaTracker } from '../helpers/analytics/user_tracking/ga';
import { MetaPixelTracker } from '../helpers/analytics/user_tracking/meta_pixel';

interface Props {
  children?: ReactNode;
  url?: string;
  title?: string;
  image?: string | null;
  userGoogleAnalyticsTrackingId?: string | null;
  isShowDownloadLink?: boolean;
  description?: string | null;
  isNoIndex?: boolean;
  linkNext?: string;
  linkPrev?: string;
  linkCanonical?: string;
  structuredData?: ReactNode;
  isCodox?: boolean;
  disableUserScalable?: boolean;
  disableGoogleTagManager?: boolean;
  locales?: string[];
  isExcludeSiteName?: boolean;
  isCorpSite?: boolean;
  userMetaPixelTrackingId?: string | null;
  customTag?: string | null;
  isUseAdsense?: boolean;
}

export default function MainDocument(props: Props) {
  const title = props.title || translate('defaultTitle');
  const siteName = props.isCorpSite
    ? translate('corpSiteName')
    : translate('siteName');
  const pageTitle = (props.isExcludeSiteName
    ? [title]
    : [title, siteName]
  ).join(' | ');
  const description =
    props.description || translate('defaultDescription', { title: title });
  const url = props.url || '';
  const image = props.image || imagePath('ogimage.png');

  const router = useRouter();

  const meta = {
    title: pageTitle,
    description,
    og: {
      title: pageTitle,
      description,
      url,
      image,
      siteName,
      type: 'website',
    },
    twitter: {
      title: pageTitle,
      description,
      url,
      image: { src: image },
      card: 'summary_large_image',
      site: '@jicoohq',
      creator: '@jicoohq',
    },
  };

  let userGoogleAnalyticsTracking: string = '';
  if (props.userGoogleAnalyticsTrackingId) {
    userGoogleAnalyticsTracking = `gtag('config', '${props.userGoogleAnalyticsTrackingId}');`;
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (typeof props.userGoogleAnalyticsTrackingId === 'string') {
        GaTracker.setUserTrackingId(props.userGoogleAnalyticsTrackingId);
      }
      if (typeof props.userMetaPixelTrackingId === 'string') {
        MetaPixelTracker.setUserTrackingId(props.userMetaPixelTrackingId);
      }
    }
  }, []);

  const availableLocales = props.locales || supportLocales;
  let isNoIndex = props.isNoIndex;
  if (process.env['BUILD_ENV'] !== 'production') {
    isNoIndex = true;
  }

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        {props.isShowDownloadLink && (
          <meta name="apple-itunes-app" content="app-id=1534672999" />
        )}
        {props.disableUserScalable && (
          <meta
            name="viewport"
            content="initial-scale=1.0, width=device-width, user-scalable=no"
          />
        )}
        <meta name="description" content={meta.description} />
        <link rel="dns-prefetch" href="//www.jicoo.com" />
        {isNoIndex === true && <meta name="robots" content="noindex" />}
        <link
          rel="icon"
          type="image/x-icon"
          href={imagePath('icons/favicon.ico')}
        />
        <link
          rel="apple-touch-icon"
          href={imagePath('icons/icon-192x192.png')}
        />
        <link rel="dns-prefetch" href="//www.google-analytics.com" />
        <link rel="dns-prefetch" href="//widget.intercom.io" />
        <link rel="dns-prefetch" href="//js.intercomcdn.com" />
        <link rel="dns-prefetch" href="//connect.facebook.net" />

        <meta property="fb:app_id" content={process.env['FB_APP_ID']} />
        <meta property="og:site_name" content={meta.og.siteName} />
        <meta property="og:title" content={meta.og.title} />
        <meta property="og:description" content={meta.og.description} />
        <meta property="og:type" content={meta.og.type} />
        <meta property="og:url" content={meta.og.url} />
        <meta property="og:image" content={meta.og.image} />
        <meta name="twitter:card" content={meta.twitter.card} />
        <meta name="twitter:site" content={meta.twitter.site} />
        <meta name="twitter:creator" content={meta.twitter.creator} />
        <meta name="twitter:title" content={meta.twitter.title} />
        <meta name="twitter:description" content={meta.twitter.description} />
        <meta name="twitter:url" content={meta.twitter.url} />
        <meta name="twitter:image:src" content={meta.twitter.image.src} />
        {availableLocales.map((locale) => (
          <link
            rel="alternate"
            hrefLang={locale}
            href={webPath(
              (locale === defaultLocale ? '' : '/' + locale) + router.asPath,
            )}
            key={locale}
          />
        ))}
        {availableLocales.includes(fallbackLocale) && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={webPath(router.asPath, fallbackLocale)}
          />
        )}
        {props.linkCanonical && (
          <link rel="canonical" href={props.linkCanonical} />
        )}
        {props.linkNext && <link rel="next" href={props.linkNext} />}
        {props.linkPrev && <link rel="prev" href={props.linkPrev} />}
        {/* {props.isCodox && (
          <>
            <link
              href="https://cdn1.codox.io/lib/2.x/css/wave.client.css"
              rel="stylesheet"
            ></link>
          </>
        )} */}
        {props.structuredData}

        <script
          dangerouslySetInnerHTML={{
            __html: !props.userMetaPixelTrackingId
              ? ''
              : `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
        `,
          }}
        />
        {/* <div dangerouslySetInnerHTML={{ __html: props.customTag || '' }} /> */}
      </Head>
      {!props.disableGoogleTagManager && process.env['GTM_TRACKING_ID'] && (
        <Script
          dangerouslySetInnerHTML={{
            __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${process.env['GTM_TRACKING_ID']}');
            `,
          }}
        />
      )}
      {props.isUseAdsense && process.env['BUILD_ENV'] === 'production' && (
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8706673872052201"
          crossOrigin="anonymous"
        ></script>
      )}

      {process.env['GA_TRACKING_ID'] && (
        <>
          <script
            src={`https://www.googletagmanager.com/gtag/js?id=${process.env['GA_TRACKING_ID']}`}
          />
          <Script
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${process.env['GA_TRACKING_ID']}',{
                custom_map: { 
                  dimension1: 'plan',
                }
              });
              window.dataLayer.push({'event': 'optimize.activate'});
              ${userGoogleAnalyticsTracking}
              `,
            }}
          />
        </>
      )}
      {/* {props.isCodox && (
        <Script src="https://cdn1.codox.io/lib/2.x/api/wave.client.js" />
      )} */}
      {!props.disableGoogleTagManager && process.env['GTM_TRACKING_ID'] && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${process.env['GTM_TRACKING_ID']}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          ></iframe>
        </noscript>
      )}
      {props.children}
    </>
  );
}
